import { useMemo } from "react";
import type { DraftPlayer } from "../../challenges/types/challengeDraft";
import { normalizeCpf } from "../../challenges/utils/challengePairs";

type UseAdminSuper8GroupsInput = {
  draftPlayers: DraftPlayer[];
  isSuper8: boolean;
  super8RankingPositionByCpf: Record<string, number>;
};

export function useAdminSuper8Groups({
  draftPlayers,
  isSuper8,
  super8RankingPositionByCpf,
}: UseAdminSuper8GroupsInput) {
  const super8OrderedPlayers = useMemo(() => {
    if (!isSuper8) return draftPlayers;
    return draftPlayers
      .map((player, sourceOrder) => {
        const cpf = normalizeCpf(player.cpf);
        const rankingPositionRaw = super8RankingPositionByCpf[cpf];
        const rankingPosition = Number.isFinite(rankingPositionRaw)
          ? Number(rankingPositionRaw)
          : Number.MAX_SAFE_INTEGER;
        return {
          ...player,
          cpf,
          sourceOrder,
          rankingPosition,
        };
      })
      .sort((a, b) => {
        if (a.rankingPosition !== b.rankingPosition) {
          return a.rankingPosition - b.rankingPosition;
        }
        return a.sourceOrder - b.sourceOrder;
      });
  }, [draftPlayers, isSuper8, super8RankingPositionByCpf]);

  const super8GroupAPlayers = useMemo(
    () => super8OrderedPlayers.slice(0, 8),
    [super8OrderedPlayers],
  );
  const super8WaitingPlayers = useMemo(
    () => super8OrderedPlayers.slice(8),
    [super8OrderedPlayers],
  );
  const super8PlayersForCurrentChallenge = useMemo(
    () => super8GroupAPlayers.slice(0, 8),
    [super8GroupAPlayers],
  );

  return {
    super8GroupAPlayers,
    super8PlayersForCurrentChallenge,
    super8WaitingPlayers,
  };
}
